/**
 * Admin Coverage Areas Screen - MOBILE ONLY
 * 
 * Manage and view supported counties and data sources.
 * Shows parcel data sources and coverage statistics.
 */

import React, { useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable, RefreshControl, ActivityIndicator, Linking } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, Redirect } from 'expo-router';
import { useAuthStore } from '../../../store/authStore';
import { useCoverage, GeoDataSource } from '../../../hooks/useCoverage';
import { color, text, space, radius, touch, icon, layout, border } from '../../../shared/tokens/mobile';
import { ScreenContainer, SectionHeader, ListCard } from '../../../components/v2/mobile';

const SOURCE_TYPE_LABELS: Record<string, string> = {
  arcgis_rest: 'ArcGIS REST',
  wfs: 'WFS',
  wms: 'WMS',
  geojson: 'GeoJSON',
  shapefile: 'Shapefile',
  api: 'API',
  manual: 'Manual',
};

export default function AdminCoverageScreen() {
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';
  const { dataSources, stats, loading, error, refresh } = useCoverage();

  const handleBack = useCallback(() => router.back(), [router]);

  const handleSourcePress = useCallback((source: GeoDataSource) => {
    if (source.source_url) {
      Linking.openURL(source.source_url);
    }
  }, []);

  if (!isAdmin) {
    return <Redirect href="/v2/home" />;
  }

  // Group sources by state
  const sourcesByState = dataSources.reduce((acc, source) => {
    const state = source.state_code || 'Other';
    if (!acc[state]) acc[state] = [];
    acc[state].push(source);
    return acc;
  }, {} as Record<string, GeoDataSource[]>);

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={refresh} tintColor={color.primary} />}
      >
        <View style={styles.header}>
          <Pressable onPress={handleBack} style={styles.backButton} accessibilityRole="button">
            <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>Coverage Areas</Text>
          <View style={styles.spacer} />
        </View>

        {/* ERROR STATE */}
        {error && (
          <View style={styles.errorBanner}>
            <Ionicons name="warning-outline" size={icon.sm} color={color.error} />
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {/* STATS */}
        <View style={styles.statsRow}>
          <StatCard value={stats.statesCovered} label="States" />
          <StatCard value={stats.countiesCovered} label="Counties" />
          <StatCard value={stats.activeSources} label="Sources" highlight={stats.activeSources > 0} />
        </View>

        {/* LOADING */}
        {loading && dataSources.length === 0 ? (
          <View style={styles.loadingState}>
            <ActivityIndicator size="large" color={color.primary} />
          </View>
        ) : dataSources.length === 0 ? (
          <View style={styles.section}>
            <ListCard>
              <View style={styles.emptyState}>
                <Ionicons name="map-outline" size={icon.xxl} color={color.textMuted} />
                <Text style={styles.emptyText}>No data sources configured</Text>
                <Text style={styles.emptySubtext}>Add parcel data sources to enable coverage</Text>
              </View>
            </ListCard>
          </View>
        ) : (
          /* DATA SOURCES BY STATE */
          Object.entries(sourcesByState).map(([state, sources]) => (
            <View key={state} style={styles.section}>
              <SectionHeader label={`${state} (${sources.length})`} />
              <ListCard>
                {sources.map((source, index) => (
                  <SourceRow
                    key={source.id}
                    source={source}
                    onPress={() => handleSourcePress(source)}
                    hideBorder={index === sources.length - 1}
                  />
                ))}
              </ListCard>
            </View>
          ))
        )}

        {/* SUPPORTED LAYER TYPES */}
        {dataSources.length > 0 && (
          <View style={styles.section}>
            <SectionHeader label="Supported Layer Types" />
            <ListCard>
              <View style={styles.layerTypesGrid}>
                <LayerTypeBadge label="Parcels" icon="map-outline" active />
                <LayerTypeBadge label="Zoning" icon="grid-outline" />
                <LayerTypeBadge label="Flood Zones" icon="water-outline" />
                <LayerTypeBadge label="Wetlands" icon="leaf-outline" />
              </View>
            </ListCard>
          </View>
        )}
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// STAT CARD
// =============================================================================

function StatCard({ value, label, highlight }: { value: number; label: string; highlight?: boolean }) {
  return (
    <View style={[styles.statCard, highlight && styles.statCardHighlight]}>
      <Text style={[styles.statValue, highlight && styles.statValueHighlight]}>{value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </View>
  );
}

// =============================================================================
// SOURCE ROW
// =============================================================================

function SourceRow({ source, onPress, hideBorder }: { source: GeoDataSource; onPress: () => void; hideBorder: boolean }) {
  const hasUrl = !!source.source_url;
  const typeLabel = SOURCE_TYPE_LABELS[source.source_type] || source.source_type;

  return (
    <Pressable
      onPress={hasUrl ? onPress : undefined}
      style={({ pressed }) => [styles.sourceRow, !hideBorder && styles.sourceRowBorder, pressed && hasUrl && styles.sourceRowPressed]}
      disabled={!hasUrl}
    >
      <View style={[styles.sourceIcon, source.is_active && styles.sourceIconActive]}>
        <Ionicons name="server-outline" size={icon.sm} color={source.is_active ? color.success : color.textMuted} />
      </View>
      <View style={styles.sourceContent}>
        <Text style={styles.sourceName} numberOfLines={1}>
          {source.county_name ? `${source.county_name} County` : source.source_name}
        </Text>
        <Text style={styles.sourceMeta} numberOfLines={1}>
          {typeLabel} • {source.source_name}
        </Text>
      </View>
      <View style={styles.sourceRight}>
        <View style={[styles.statusIndicator, { backgroundColor: source.is_active ? color.success : color.textMuted }]} />
        {hasUrl && <Ionicons name="open-outline" size={icon.sm} color={color.textMuted} />}
      </View>
    </Pressable>
  );
}

// =============================================================================
// LAYER TYPE BADGE
// =============================================================================

function LayerTypeBadge({ label, icon: iconName, active }: { label: string; icon: keyof typeof Ionicons.glyphMap; active?: boolean }) {
  return (
    <View style={[styles.layerBadge, active && styles.layerBadgeActive]}>
      <Ionicons name={iconName} size={icon.md} color={active ? color.primary : color.textMuted} />
      <Text style={[styles.layerBadgeText, active && styles.layerBadgeTextActive]}>{label}</Text>
    </View>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: layout.scrollBottomPadding },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: space.sm, paddingVertical: space.sm },
  backButton: { width: touch.minimum, height: touch.minimum, alignItems: 'center', justifyContent: 'center' },
  title: { flex: 1, fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary, textAlign: 'center' },
  spacer: { width: touch.minimum },
  section: { paddingHorizontal: space.md, marginBottom: space.lg },
  errorBanner: { flexDirection: 'row', alignItems: 'center', gap: space.sm, marginHorizontal: space.md, marginBottom: space.md, padding: space.md, backgroundColor: color.errorLight, borderRadius: radius.md },
  errorText: { flex: 1, fontSize: text.caption.fontSize, color: color.error },
  statsRow: { flexDirection: 'row', paddingHorizontal: space.md, marginBottom: space.lg, gap: space.sm },
  statCard: { flex: 1, backgroundColor: color.surface, borderRadius: radius.md, padding: space.md, alignItems: 'center', borderWidth: 1, borderColor: color.border },
  statCardHighlight: { backgroundColor: color.successLight, borderColor: color.success },
  statValue: { fontSize: text.sectionTitle.fontSize, fontWeight: text.sectionTitle.fontWeight, color: color.textPrimary },
  statValueHighlight: { color: color.success },
  statLabel: { fontSize: text.caption.fontSize, color: color.textSecondary, marginTop: space.xs },
  loadingState: { paddingVertical: space.xxl, alignItems: 'center' },
  emptyState: { paddingVertical: space.xxl, alignItems: 'center', gap: space.md },
  emptyText: { fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary },
  emptySubtext: { fontSize: text.caption.fontSize, color: color.textMuted },
  sourceRow: { flexDirection: 'row', alignItems: 'center', padding: space.md, gap: space.sm },
  sourceRowBorder: { borderBottomWidth: border.width.thin, borderBottomColor: color.divider },
  sourceRowPressed: { backgroundColor: color.pressed },
  sourceIcon: { width: 32, height: 32, borderRadius: 16, backgroundColor: color.backgroundSecondary, alignItems: 'center', justifyContent: 'center' },
  sourceIconActive: { backgroundColor: color.successLight },
  sourceContent: { flex: 1 },
  sourceName: { fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary },
  sourceMeta: { fontSize: text.caption.fontSize, color: color.textSecondary, marginTop: 2 },
  sourceRight: { flexDirection: 'row', alignItems: 'center', gap: space.sm },
  statusIndicator: { width: 8, height: 8, borderRadius: 4 },
  layerTypesGrid: { flexDirection: 'row', flexWrap: 'wrap', padding: space.md, gap: space.sm },
  layerBadge: { flexDirection: 'row', alignItems: 'center', gap: space.xs, paddingVertical: space.xs, paddingHorizontal: space.sm, borderRadius: radius.md, backgroundColor: color.backgroundSecondary },
  layerBadgeActive: { backgroundColor: color.primaryLight },
  layerBadgeText: { fontSize: text.caption.fontSize, color: color.textMuted },
  layerBadgeTextActive: { color: color.primary, fontWeight: '500' },
});
